// Binding arbitrary points to a skinned mesh — the pure, general half.
//
// Two consumers need the same operation and neither is the other's special
// case: a hair strand binds its ROOT and shares those influences along its
// length (hairStrandSkin.js), while a garment binds EVERY vertex it has
// (garment/garmentBind.js). What they share is the primitive underneath —
// find the nearest skinned vertex, take its heaviest influences, and report
// loudly when a point landed nowhere near the skin.
//
// These three symbols lived in hairStrandSkin.js until the garment importer
// (ENG-1108) needed them; hairStrandSkin.js re-exports them, so every existing
// import still resolves and its tests still drive them by that name.
//
// No three.js import here, so node:test drives the maths.

/**
 * Uniform-grid nearest-point index over a flat xyz array. Cells are keyed by
 * integer coordinates; a query scans growing cubes of cells and stops as soon
 * as the best hit is provably nearer than anything an unscanned cell can hold.
 */
export class VertexGrid {
  /**
   * @param {ArrayLike<number>} positions  xyz triples
   * @param {{ cell?: number }} [opts]  cell size; default ~1.5x the mean vertex spacing
   */
  constructor(positions, { cell } = {}) {
    const n = Math.floor(positions.length / 3)
    this.positions = positions
    this.count = n
    this.lastDist = Infinity
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
    for (let i = 0; i < n; i++) {
      const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2]
      if (x < minX) minX = x; if (x > maxX) maxX = x
      if (y < minY) minY = y; if (y > maxY) maxY = y
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z
    }
    if (n === 0) { minX = minY = minZ = 0; maxX = maxY = maxZ = 0 }
    const volume = Math.max((maxX - minX) * (maxY - minY) * (maxZ - minZ), 1e-12)
    this.cell = cell ?? Math.max(1e-5, Math.cbrt(volume / Math.max(n, 1)) * 1.5)
    this.min = [minX, minY, minZ]
    const inv = 1 / this.cell
    this.dims = [
      Math.max(1, Math.floor((maxX - minX) * inv) + 1),
      Math.max(1, Math.floor((maxY - minY) * inv) + 1),
      Math.max(1, Math.floor((maxZ - minZ) * inv) + 1),
    ]
    // Counting sort of vertices into cells: cellStart[c]..cellStart[c+1] index into `order`.
    const cells = this.dims[0] * this.dims[1] * this.dims[2]
    const cellOf = new Uint32Array(n)
    const counts = new Uint32Array(cells + 1)
    for (let i = 0; i < n; i++) {
      const c = this._cellIndex(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2])
      cellOf[i] = c
      counts[c + 1]++
    }
    for (let c = 0; c < cells; c++) counts[c + 1] += counts[c]
    const fill = counts.slice(0, cells)
    const order = new Uint32Array(n)
    for (let i = 0; i < n; i++) order[fill[cellOf[i]]++] = i
    this.cellStart = counts
    this.order = order
  }

  _cellCoord(v, axis) {
    const c = Math.floor((v - this.min[axis]) / this.cell)
    return c < 0 ? 0 : c >= this.dims[axis] ? this.dims[axis] - 1 : c
  }

  _cellIndex(x, y, z) {
    return (this._cellCoord(z, 2) * this.dims[1] + this._cellCoord(y, 1)) * this.dims[0] + this._cellCoord(x, 0)
  }

  /**
   * Nearest vertex to (x,y,z). Scans cubes of radius 0, 1, 2... cells around the
   * query and returns once the best distance is <= radius * cell (nothing in a
   * further ring can beat it). `maxRings` bounds the search; past it the best
   * seen so far (or -1 when nothing) is returned. The distance is left in
   * `this.lastDist`.
   * @returns {number} vertex index or -1
   */
  nearest(x, y, z, maxRings = 6) {
    const { positions, dims, cellStart, order, cell } = this
    const cx = this._cellCoord(x, 0), cy = this._cellCoord(y, 1), cz = this._cellCoord(z, 2)
    let best = -1, bestD2 = Infinity
    for (let r = 0; r <= maxRings; r++) {
      const x0 = Math.max(0, cx - r), x1 = Math.min(dims[0] - 1, cx + r)
      const y0 = Math.max(0, cy - r), y1 = Math.min(dims[1] - 1, cy + r)
      const z0 = Math.max(0, cz - r), z1 = Math.min(dims[2] - 1, cz + r)
      for (let iz = z0; iz <= z1; iz++) {
        const shellZ = iz === cz - r || iz === cz + r
        for (let iy = y0; iy <= y1; iy++) {
          const shellY = shellZ || iy === cy - r || iy === cy + r
          for (let ix = x0; ix <= x1; ix++) {
            // Only the shell of radius r is new; the inner cube was scanned already.
            if (!(shellY || ix === cx - r || ix === cx + r)) continue
            const c = (iz * dims[1] + iy) * dims[0] + ix
            for (let k = cellStart[c], e = cellStart[c + 1]; k < e; k++) {
              const i = order[k]
              const dx = positions[i * 3] - x, dy = positions[i * 3 + 1] - y, dz = positions[i * 3 + 2] - z
              const d2 = dx * dx + dy * dy + dz * dz
              if (d2 < bestD2) { bestD2 = d2; best = i }
            }
          }
        }
      }
      if (best >= 0 && bestD2 <= (r * cell) * (r * cell)) break
      // Every cell is in: nothing further to scan.
      if (x0 === 0 && y0 === 0 && z0 === 0 && x1 === dims[0] - 1 && y1 === dims[1] - 1 && z1 === dims[2] - 1) break
    }
    this.lastDist = best >= 0 ? Math.sqrt(bestD2) : Infinity
    return best
  }
}

/**
 * The `k` heaviest joint influences of vertex `v` across every skin set,
 * renormalised to sum to 1. `sets` is `[{ index, weight }]`, each an array-like
 * of vec4s (three's skinIndex/skinWeight layout). Writes indices and weights at
 * `o`; returns false (and writes bone 0 at weight 1) when the vertex carries no
 * weight at all.
 */
export function topInfluences(sets, v, k, outIndex, outWeight, o = 0) {
  // Gather all influences, then partial-select the k largest (sets*4 is at most 12).
  const idx = [], w = []
  for (const set of sets) {
    for (let c = 0; c < 4; c++) {
      const wt = set.weight[v * 4 + c]
      if (wt > 0) { idx.push(set.index[v * 4 + c]); w.push(wt) }
    }
  }
  let total = 0
  for (let j = 0; j < k; j++) {
    let bi = -1, bw = 0
    for (let i = 0; i < w.length; i++) if (w[i] > bw) { bw = w[i]; bi = i }
    if (bi < 0) { outIndex[o + j] = 0; outWeight[o + j] = 0; continue }
    outIndex[o + j] = idx[bi]; outWeight[o + j] = bw; total += bw
    w[bi] = 0
  }
  if (total <= 0) { outIndex[o] = 0; outWeight[o] = 1; return false }
  for (let j = 0; j < k; j++) outWeight[o + j] /= total
  return true
}

/**
 * Binds a cloud of points to the nearest skinned vertex of a body.
 *
 * `maxDist` is the honesty knob: a point further than this from any skinned
 * vertex is NOT on this body (a flared hem, a prop, a garment authored for a
 * different figure), and binding it to whatever happened to be nearest would
 * drag it across the character on the first frame the skeleton moves. Such a
 * point rides `fallbackBone` rigidly instead and is COUNTED, so the caller can
 * say how much of the mesh it failed to bind rather than shipping a silent
 * mess.
 *
 * @param {object} args
 * @param {ArrayLike<number>} args.points   xyz triples, in the grid's space
 * @param {VertexGrid} args.grid            skinned vertices, same space
 * @param {(v:number, outIndex:Float32Array, outWeight:Float32Array, o:number)=>void} args.influencesAt
 * @param {number} args.fallbackBone        bone a point too far from the skin rides rigidly
 * @param {number} [args.influences]        influences per point; default 4
 * @param {number} [args.maxDist]           metres; beyond this a point is not on this skin
 * @returns {{ skinIndex: Float32Array, skinWeight: Float32Array, dists: Float32Array,
 *             stats: { points:number, bound:number, unbound:number, medianDist:number, p95Dist:number, maxDist:number } }}
 */
export function bindPointsToSkin({ points, grid, influencesAt, fallbackBone, influences = 4, maxDist = 0.05 }) {
  const K = influences
  const n = Math.floor(points.length / 3)
  const skinIndex = new Float32Array(n * K)
  const skinWeight = new Float32Array(n * K)
  const dists = new Float32Array(n)
  let unbound = 0
  for (let i = 0; i < n; i++) {
    const v = grid.nearest(points[i * 3], points[i * 3 + 1], points[i * 3 + 2])
    const d = grid.lastDist
    dists[i] = Number.isFinite(d) ? d : maxDist
    if (v < 0 || d > maxDist) {
      skinIndex[i * K] = fallbackBone
      skinWeight[i * K] = 1
      unbound++
      continue
    }
    influencesAt(v, skinIndex, skinWeight, i * K)
  }
  const sorted = Float32Array.from(dists).sort()
  const at = (q) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] : 0)
  return {
    skinIndex,
    skinWeight,
    dists,
    stats: {
      points: n,
      bound: n - unbound,
      unbound,
      medianDist: at(0.5),
      p95Dist: at(0.95),
      maxDist: sorted.length ? sorted[sorted.length - 1] : 0,
    },
  }
}
